// Predict tonight's sunset quality based on cloud fraction and humidity.
// High/mid clouds with low humidity = vivid sunset. Heavy low cloud = washed out.

export interface SunsetInput {
  // Use forecast hour closest to sunset.
  cloudPct: number; // 0..100 total cloud cover
  humidity: number; // 0..100
  visibilityMeters?: number;
}

export interface SunsetQuality {
  score: number; // 0-10
  label: "Spectacular" | "Vivid" | "Decent" | "Muted" | "Washed out";
  emoji: string;
  reason: string;
}

export function scoreSunset(input: SunsetInput): SunsetQuality {
  const c = input.cloudPct;
  // Cloud sweet spot: 30-70% (mid-high clouds catch color).
  let cloudScore: number;
  if (c < 10) cloudScore = 5; // dead clear — pretty but flat
  else if (c < 30) cloudScore = 7;
  else if (c <= 70) cloudScore = 10;
  else if (c <= 90) cloudScore = 5;
  else cloudScore = 2; // total overcast

  // Humidity: drier air = more vivid colors.
  let humidScore: number;
  if (input.humidity <= 40) humidScore = 10;
  else if (input.humidity <= 60) humidScore = 8;
  else if (input.humidity <= 80) humidScore = 5;
  else humidScore = 3;

  // Visibility (optional): haze hurts.
  let visScore = 8;
  if (input.visibilityMeters != null) {
    if (input.visibilityMeters >= 15000) visScore = 10;
    else if (input.visibilityMeters >= 8000) visScore = 8;
    else if (input.visibilityMeters >= 4000) visScore = 5;
    else visScore = 2;
  }

  const raw = cloudScore * 0.55 + humidScore * 0.25 + visScore * 0.2;
  const score = Math.round(Math.max(0, Math.min(10, raw)) * 10) / 10;

  const label: SunsetQuality["label"] =
    score >= 8.5
      ? "Spectacular"
      : score >= 7
        ? "Vivid"
        : score >= 5
          ? "Decent"
          : score >= 3
            ? "Muted"
            : "Washed out";

  const emoji =
    score >= 8 ? "🌅" : score >= 5 ? "🌇" : "☁️";

  const reasons: string[] = [];
  if (c >= 30 && c <= 70) reasons.push("mid clouds for color");
  else if (c < 10) reasons.push("clear, will be flat");
  else if (c > 90) reasons.push("overcast");
  if (input.humidity <= 50) reasons.push("dry air");
  else if (input.humidity > 80) reasons.push("humid haze");

  return {
    score,
    label,
    emoji,
    reason: reasons.join(" · ") || "average conditions",
  };
}
